import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowRight } from 'react-icons/fi';
import { valueMessageItems, pickLang } from '../../data/playbookStory';
import { trackConversion } from '../../lib/analytics';

/**
 * Mensaje principal dinámico de la home: lo que InvokeApp entrega YA resuelto.
 *
 * Efecto de rotación vertical tipo selector cilíndrico (iOS): el concepto activo
 * queda centrado y los vecinos se van por arriba y por abajo con rotateX. Se usa
 * UNA sola interacción, sin escritura ni carrusel encima, como se pidió.
 *
 * Decisiones que no se ven a simple vista:
 *  - Cada concepto trae su propio remate ("ya resuelto" / "ya resueltas"): en
 *    castellano un sufijo fijo NO concuerda en género y número, y quedaría
 *    "las credenciales ya resuelto".
 *  - Solo se pintan los vecinos cercanos (±2): el resto no existe en el DOM.
 *  - La rotación se PARA cuando la sección no está en pantalla
 *    (IntersectionObserver), para no dejar un timer corriendo toda la sesión.
 *  - Con prefers-reduced-motion no hay rotación: se queda un concepto fijo y
 *    el mensaje se entiende igual.
 */
const ROTATE_MS = 2600;
const VISIBLE_NEIGHBOURS = 2;

const prefersReducedMotion = () => {
   try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
   } catch (e) {
      return false;
   }
};

const DynamicValueMessage = () => {
   const { t, i18n } = useTranslation();
   const lang = i18n?.language === 'en' ? 'en' : 'es';
   const [index, setIndex] = useState(0);
   const [inView, setInView] = useState(false);
   const sectionRef = useRef(null);
   const reduced = useRef(prefersReducedMotion());

   // Solo rota mientras se ve.
   useEffect(() => {
      const node = sectionRef.current;
      if (!node || typeof IntersectionObserver === 'undefined') {
         setInView(true);
         return undefined;
      }
      const observer = new IntersectionObserver(
         (entries) => setInView(entries[0].isIntersecting),
         { threshold: 0.25 }
      );
      observer.observe(node);
      return () => observer.disconnect();
   }, []);

   useEffect(() => {
      if (!inView || reduced.current) return undefined;
      const timer = setInterval(
         () => setIndex((prev) => (prev + 1) % valueMessageItems.length),
         ROTATE_MS
      );
      return () => clearInterval(timer);
   }, [inView]);

   return (
      <section className="dvm pt-80 pb-80" ref={sectionRef}>
         <div className="container">
            <div className="row">
               <div className="col-xl-10 offset-xl-1 text-center">
                  <h2 className="dvm__headline">{t('dvm_headline')}</h2>

                  <p className="dvm__dynamic">
                     <span className="dvm__prefix">{t('dvm_prefix')}</span>
                     {/* Un lector de pantalla no debe oír la rueda girando (y
                         quedarse solo con el concepto que pase en ese momento):
                         se le da la lista COMPLETA de una vez, que es el mensaje
                         real, y la rueda queda como pura decoración. */}
                     <span className="visually-hidden">
                        {valueMessageItems
                           .map((item) => pickLang(item.concept, lang))
                           .join(', ')}
                        . {t('dvm_all_solved')}
                     </span>

                     <span className="dvm__wheel" aria-hidden="true">
                        {valueMessageItems.map((item, i) => {
                           let offset = i - index;
                           const half = valueMessageItems.length / 2;
                           // Camino más corto en el cilindro (para que al dar la
                           // vuelta no viaje de golpe de abajo del todo a arriba).
                           if (offset > half) offset -= valueMessageItems.length;
                           if (offset < -half) offset += valueMessageItems.length;
                           if (Math.abs(offset) > VISIBLE_NEIGHBOURS) return null;
                           return (
                              <span
                                 key={item.key}
                                 className={`dvm__item${offset === 0 ? ' is-active' : ''}`}
                                 style={{
                                    '--offset': offset,
                                    opacity: offset === 0 ? 1 : 0.16 / Math.abs(offset),
                                 }}
                              >
                                 <span className="dvm__concept">
                                    {pickLang(item.concept, lang)}
                                 </span>{' '}
                                 <span className="dvm__suffix">
                                    {pickLang(item.suffix, lang)}
                                 </span>
                              </span>
                           );
                        })}
                     </span>
                  </p>

                  <p className="dvm__lead">{t('dvm_lead')}</p>

                  <div className="dvm__actions">
                     <Link
                        to="/playbooks"
                        className="z-btn z-btn-3"
                        onClick={() => trackConversion('Home DVM - Playbooks')}
                     >
                        {t('dvm_cta_playbooks')} <FiArrowRight size={15} />
                     </Link>
                     <Link
                        to="/services"
                        className="dvm__btn-ghost"
                        onClick={() => trackConversion('Home DVM - Catalog')}
                     >
                        {t('dvm_cta_catalog')}
                     </Link>
                  </div>
               </div>
            </div>
         </div>
      </section>
   );
};

export default DynamicValueMessage;
